import type { ClassCount } from '@visionqc/shared';

/**
 * What the line decided each crate was, ranked — horizontal bars.
 *
 * Horizontal because cut names are prose ("Schouder zonder been") and a ranked
 * magnitude reads best against a text column.
 *
 * ONE HUE, NOT A PALETTE. Defect bars give every defect its own colour because
 * that colour ties back to the annotator's own vocabulary. Classes have no such
 * colour and inventing one per class would encode identity in a hue that means
 * nothing — twenty-four cuts would exhaust any categorical ramp and start
 * cycling, at which point two different cuts share a colour on a screen read
 * from two metres. The name carries identity; the bar carries only how many.
 *
 * Counted on what came off the line — the corrected class where a person
 * corrected one — not on what the model believed.
 */
export function ClassBars({ classes }: { classes: ClassCount[] }) {
  if (classes.length === 0) {
    return (
      <div className="muted" style={{ padding: '20px 0' }}>
        Nothing classified in this window.
      </div>
    );
  }
  const max = Math.max(...classes.map((c) => c.count));
  const total = classes.reduce((s, c) => s + c.count, 0);

  return (
    <div className="grid" style={{ gap: 10 }}>
      {classes.map((c) => (
        <div key={`${c.classId}-${c.code}`}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
            <span style={{ fontSize: 14, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12, marginRight: 8 }}>
                {c.code}
              </span>
              {c.name}
            </span>
            <span className="row" style={{ gap: 10, flex: 'none' }}>
              {/*
                Mean confidence beside the count, because the two answer
                different questions. A class the model names often but names
                doubtfully is the one to look at, and a count alone hides it
                behind a reassuring bar.
              */}
              <span
                className="muted"
                style={{ fontFamily: 'var(--mono)', fontSize: 12 }}
                title="Mean confidence of the predictions in this class"
              >
                {(c.avgConfidence * 100).toFixed(0)}%
              </span>
              <strong style={{ fontFamily: 'var(--mono)', fontSize: 14 }}>{c.count}</strong>
              <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12, width: 44, textAlign: 'right' }}>
                {total > 0 ? `${((c.count / total) * 100).toFixed(1)}%` : '—'}
              </span>
            </span>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 4, height: 10 }}>
            <div
              style={{
                width: `${(c.count / max) * 100}%`,
                height: '100%',
                background: 'var(--accent)',
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
